import PropTypes from "prop-types";

import { useCallback, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { arrayUtil } from "../../../utils";
import { dispatch } from "../../../state/store";
import { setFWUFilters } from "../../../state/ducks";
import { FiltersWrapper } from "../../UserManagement/UsersTable/components/FiltersTab/styled";
import { FilterDropdown, FilterInput, FlexContainers, TextButton } from "../../../components";
import {
  allDeviceTypeOption,
  deviceTypeOptions as allDeviceTypeOptions,
  deviceGroupNames,
  strCode
} from "../../../consts";
import { t } from "../../../services";
import { deviceModels } from "../../Campaigns/consts/deviceModels";
import DeviceCellComponent from "./components/DeviceCellComponent";

const CampaignFiltersTab = ({ visible, setActiveFilters }) => {
  const markets = useSelector((store) => store.markets?.markets) ?? [];

  const filters = useSelector((store) => store.fleetManagement?.fwuCampaignFilters);

  const {
    devices: selectedDevices,
    deviceType: selectedDeviceType,
    markets: selectedMarkets
  } = filters;

  const marketOptions = useMemo(() => {
    let options = markets.map((market) => {
      return {
        id: market.market,
        value: market.market
      };
    });
    return options;
  }, [markets.length]);

  const deviceModelOptions = useMemo(() => {
    return Object.keys(deviceModels)?.map((device) => {
      return { id: device, value: device, type: deviceGroupNames.charger };
    });
  }, []);

  const initialState = useMemo(() => {
    return {
      devices: [],
      deviceType: allDeviceTypeOption.id,
      markets: []
    };
  }, []);

  const changedState = useMemo(() => {
    return {
      deviceType: selectedDeviceType !== initialState.deviceType,
      devices: !arrayUtil.arrayEquals(selectedDevices ?? [], initialState.devices),
      markets: !arrayUtil.arrayEquals(selectedMarkets ?? [], initialState.markets)
    };
  }, [selectedDeviceType, selectedDevices, selectedMarkets, initialState]);

  useEffect(() => {
    const activeFilters = Object.values(changedState).reduce(
      (acc, value) => (acc += value ? 1 : 0),
      0
    );

    setActiveFilters(activeFilters);
  }, [changedState]);

  const onMarketsChanged = (ids) => {
    const selectedMarketsOptions = marketOptions.filter((x) => ids.includes(x.id)).map((x) => x.id);
    dispatch(
      setFWUFilters({
        markets: selectedMarketsOptions ?? []
      })
    );
  };

  const onDevicesChanged = (ids) => {
    const selectedMarketsOptions = deviceModelOptions
      .filter((x) => ids.includes(x.id))
      .map((x) => x.id);
    dispatch(
      setFWUFilters({
        devices: selectedMarketsOptions ?? []
      })
    );
  };

  const onDeviceTypeChanged = (id) => {
    dispatch(
      setFWUFilters({
        deviceType: id
      })
    );
  };

  const deviceTypeOptions = useMemo(() => [allDeviceTypeOption, ...allDeviceTypeOptions], []);

  const clearFilters = useCallback(() => {
    dispatch(
      setFWUFilters({
        devices: initialState.devices,
        deviceType: initialState.deviceType,
        markets: initialState.markets
      })
    );
  }, [initialState]);

  useEffect(() => {
    if (!selectedDeviceType && !selectedDevices && !selectedMarkets) {
      clearFilters();
    }
  }, [clearFilters, initialState]);

  return (
    <FiltersWrapper
      Container={FlexContainers.FlexRowStart}
      width={"100%"}
      gap={24}
      mb={14}
      visible={visible}>
      {deviceModelOptions.length > 1 && (
        <FilterInput
          options={deviceModelOptions}
          isActive={changedState.devices}
          selectedOptions={deviceModelOptions.filter((x) => selectedDevices?.includes(x.id))}
          allOptionsText={
            deviceModelOptions.length === 1
              ? deviceModelOptions[0].value
              : strCode.ALL_DEVICES_LABEL
          }
          title={t(strCode.DEVICE_SEARCH_TITLE)}
          description={t(strCode.DEVICE_SEARCH_DESCRIPTION)}
          searchPlaceholder={t(strCode.DEVICE_SEARCH_PLACEHOLDER)}
          columnName={t(strCode.DEVICE_MODEL_TITLE)}
          onSelectedOptionsChanged={onDevicesChanged}
          CellComponent={DeviceCellComponent}
        />
      )}

      <FilterDropdown
        active={changedState.deviceType}
        options={deviceTypeOptions}
        optionsListWidth={"100%"}
        onSelectOption={onDeviceTypeChanged}
        selectedOption={deviceTypeOptions.find((x) => x.id === selectedDeviceType)}
        InputComponent={
          <span>{t(deviceTypeOptions.find((x) => x.id === selectedDeviceType)?.value)}</span>
        }
      />

      {marketOptions.length > 1 && (
        <FilterInput
          options={marketOptions}
          isActive={changedState.markets}
          selectedOptions={marketOptions.filter((x) => selectedMarkets?.includes(x.id))}
          allOptionsText={
            marketOptions.length === 1 ? marketOptions[0].value : strCode.ALL_MARKETS_TITLE
          }
          title={t(strCode.MARKETS_SEARCH_TITLE)}
          description={t(strCode.MARKETS_SEARCH_DESCRIPTION)}
          searchPlaceholder={t(strCode.MARKETS_SEARCH_PLACEHOLDER)}
          columnName={t(strCode.MARKET_NAMES)}
          onSelectedOptionsChanged={onMarketsChanged}
        />
      )}

      {(changedState.deviceType || changedState.devices || changedState.markets) && (
        <TextButton onClick={clearFilters}>{t(strCode.CLEAR_BTN)}</TextButton>
      )}
    </FiltersWrapper>
  );
};

CampaignFiltersTab.propTypes = {
  visible: PropTypes.bool,
  setActiveFilters: PropTypes.func.isRequired
};

export default CampaignFiltersTab;
